import localhost from "apis/localhost";
import { NEW_DOC_PATH } from "components/WritingEditing/constants";
import { DOC_SOURCE } from "constants/documents";
import { showErrorModal } from "slices/app/actions/error";
import { addToJobQueue, removeFromJobQueue } from "slices/app/appSlice";
import { selectUsername } from "slices/app/selectors";
import { startSSEConnection } from "slices/workspace/actions/uploadFiles";

import {
  clearDocumentData,
  setDocumentData,
} from "../currentDocument/currentDocumentSlice";
import { selectCurrentDocument } from "../selectors";
import {
  DOCUMENT_EDITOR_CONFIG,
  removeDocument,
  setDocuments,
  setReadabilityScores,
  setReadabilityThresholds,
} from "./documentsSlice";

export const getDocumentList = (originId, entityType) => async (dispatch) => {
  const { endpoint, listEndpoint, listParams, paramKey } =
    DOCUMENT_EDITOR_CONFIG[entityType];
  const url = listEndpoint || endpoint;

  try {
    const response = await localhost.get(url, {
      params: {
        [paramKey]: originId,
        ...listParams,
      },
    });

    const { data } = response;
    dispatch(setDocuments({ entityType, documents: data }));
  } catch (error) {
    dispatch(showErrorModal(error));
  }
};

export const fetchDocumentDeleteCheck = (id) => async (dispatch) => {
  try {
    const response = await localhost.get(
      `/nextgen/document/${id}/delete-check`
    );
    return response?.data || {};
  } catch (error) {
    dispatch(showErrorModal(error));
    return null;
  }
};

export const deleteDocument = (id, entityType) => async (dispatch) => {
  dispatch(addToJobQueue("deleteDocument"));
  const { endpoint } = DOCUMENT_EDITOR_CONFIG[entityType];

  try {
    await localhost.delete(`${endpoint}/${id}`);
    dispatch(removeDocument({ entityType, id }));
    return true;
  } catch (error) {
    dispatch(showErrorModal(error));
    return false;
  } finally {
    dispatch(removeFromJobQueue("deleteDocument"));
  }
};

export const lockDocument = (id, entityType) => async (dispatch) => {
  dispatch(addToJobQueue("lockDocument"));

  const { endpoint } = DOCUMENT_EDITOR_CONFIG[entityType];

  try {
    await localhost.get(`${endpoint}/document/lock`, {
      params: {
        docId: id,
      },
    });
    return true;
  } catch (error) {
    dispatch(showErrorModal(error, "Cannot open document"));
    return false;
  } finally {
    dispatch(removeFromJobQueue("lockDocument"));
  }
};

export const unlockDocument =
  (id, entityType, originId) => async (dispatch) => {
    dispatch(addToJobQueue("unlockDocument"));

    const { endpoint } = DOCUMENT_EDITOR_CONFIG[entityType];

    try {
      await localhost.get(`${endpoint}/document/unlock`, {
        params: {
          docId: id,
        },
      });

      dispatch(getDocumentList(originId, entityType));
    } catch (error) {
      dispatch(showErrorModal(error, "Failed to unlock document"));
    } finally {
      dispatch(removeFromJobQueue("unlockDocument"));
    }
  };

export const getDocumentEditorUrl =
  (doc, entityType, entityId, isArchived = false) =>
  () => {
    const { id: docId } = doc;

    const { getEditorUrl } = DOCUMENT_EDITOR_CONFIG[entityType];
    return getEditorUrl(entityId, docId, isArchived);
  };

export const fetchDocumentForEditingById =
  (
    documentId,
    originId,
    entityType,
    { failSilently = false, lock = true, isArchived } = {}
  ) =>
  async (dispatch, getState) => {
    dispatch(addToJobQueue("fetchDocumentForEditing"));
    const user = selectUsername(getState());

    if (!entityType) {
      return;
    }

    const { endpoint, paramKey } = DOCUMENT_EDITOR_CONFIG[entityType];
    const url = `${endpoint}/${documentId}`;

    try {
      const rsp = await localhost.get(url, {
        params: {
          [paramKey]: originId,
        },
      });

      const data = rsp?.data || {};
      const isLockedByOther = Boolean(data?.isLockedByAnotherUser);
      const isAddInSourced = data?.source === DOC_SOURCE.ADDIN;

      if (lock && !isLockedByOther && !isAddInSourced && !isArchived) {
        await dispatch(lockDocument(documentId, entityType));
        data.isLockedBy = user;
      }

      if (data.flowId) {
        data.isSyncing = true;
        dispatch(startSSEConnection(data.flowId));
      }
      dispatch(setDocumentData(data));
      return data;
    } catch (error) {
      if (!failSilently) {
        dispatch(showErrorModal(error));
      }
      return error;
    } finally {
      dispatch(removeFromJobQueue("fetchDocumentForEditing"));
    }
  };

export const clearDocumentForEditingById =
  (documentId, originId, entityType) => async (dispatch, getState) => {
    const state = getState();
    const isNewDocument = documentId === NEW_DOC_PATH;
    const data = selectCurrentDocument(state);

    dispatch(clearDocumentData());

    if (isNewDocument) {
      return;
    }

    if (data.id && !data.isLockedByAppUser) {
      await dispatch(unlockDocument(documentId, entityType, originId));
    }
  };

export const fetchReadabilityScores =
  (entityId, entityType) => async (dispatch) => {
    const { endpoint } = DOCUMENT_EDITOR_CONFIG[entityType];
    dispatch(addToJobQueue("fetchReadabilityScores"));
    try {
      const response = await localhost.get(
        `${endpoint}/${entityId}/readability-scores`
      );
      const { scores, thresholds } = response.data;
      const scoresMap = Object.fromEntries(scores.map((s) => [s.docId, s]));
      dispatch(setReadabilityScores({ entityType, scores: scoresMap }));
      dispatch(setReadabilityThresholds({ entityType, thresholds }));
    } catch (error) {
      dispatch(showErrorModal(error));
    } finally {
      dispatch(removeFromJobQueue("fetchReadabilityScores"));
    }
  };
